import { AssessmentType } from '@g17eco/types/survey';
import {
  AssessmentData,
  DoubleMaterialityAssessmentData,
  EESGCategory,
  MaterialityAssessmentScope,
  MaterialityBoundary,
  MaterialitySurveyModelMinData,
  MaterialPillar,
  MaterialTopicCategories,
} from './api/materiality-assessment';
import { PrioritizedAssessmentData } from './components/assessment-insights/types';
import { assessmentTypeLabels } from '../../utils/materiality';
import { SurveyType } from '@g17eco/types/surveyCommon';
import type { MinimalSurveyData } from './components/assessment/types';

export interface AssessmentDataRow {
  name?: string;
  score: number;
  relativeScore: number | undefined;
  utrMapping: string;
  esgs: string;
  sdgs: string;
  materialPillars: string;
  materialBoundaries: string;
  financialScore?: number;
  financialRelativeScore?: number;
  nonFinancialScore?: number;
  nonFinancialRelativeScore?: number;
}

export enum CompanySettingsPages {
  Details = 'details',
  DefaultSurveyConfig = 'default-survey-config',
}

type AssessmentDataRowKey = keyof AssessmentDataRow;

const defaultCategories: MaterialTopicCategories = { boundary: [], esg: [], materialPillar: [], sdg: [] };

export const convertResult = (data: AssessmentData[] | DoubleMaterialityAssessmentData[]): AssessmentDataRow[] =>
  data.map((row) => {
    const { categories = defaultCategories, utrMapping = [], name, score } = row;
    return {
      name,
      score,
      relativeScore: row.relativeScore,
      financialScore: 'financialScore' in row ? row.financialScore : undefined,
      financialRelativeScore: 'financialRelativeScore' in row ? row.financialRelativeScore : undefined,
      nonFinancialScore: 'nonFinancialScore' in row ? row.nonFinancialScore : undefined,
      nonFinancialRelativeScore: 'nonFinancialRelativeScore' in row ? row.nonFinancialRelativeScore : undefined,
      utrMapping: utrMapping.map((utr) => utr.code).join(', '),
      esgs: (categories.esg ?? []).join(', '),
      sdgs: (categories.sdg ?? []).join(', '),
      materialPillars: (categories.materialPillar ?? []).join(', '),
      materialBoundaries: (categories.boundary ?? []).join(', '),
    };
  });

export const assessmentColumnMap: {
  [key in keyof Omit<AssessmentDataRow, 'financialScore' | 'nonFinancialScore'>]: string;
} = {
  name: 'Material topic',
  score: 'Materiality score',
  relativeScore: 'Relevance score',
  utrMapping: 'Mapped UTR codes',
  esgs: 'ESGs',
  sdgs: 'SDGs',
  materialPillars: 'Material pillars',
  materialBoundaries: 'Material boundaries',
};

export const doubleMaterialityColumnMap: { [key in AssessmentDataRowKey]: string } = {
  name: 'Material topic',
  score: 'Total materiality score',
  relativeScore: 'Total relevance score',
  financialScore: 'Financial materiality score',
  financialRelativeScore: 'Financial relevance score',
  nonFinancialScore: 'Impact materiality score',
  nonFinancialRelativeScore: 'Impact relevance score',
  utrMapping: 'Mapped UTR codes',
  esgs: 'ESGs',
  sdgs: 'SDGs',
  materialPillars: 'Material pillars',
  materialBoundaries: 'Material boundaries',
};

export const esgMap = {
  [EESGCategory.Environmental]: { label: 'Environment', icon: 'fa-leaf', color: 'text-ThemeSuccessMedium' },
  [EESGCategory.Social]: { label: 'Social', icon: 'fa-thumbs-up', color: 'text-ThemeChartMedium' },
  [EESGCategory.Governance]: { label: 'Governance', icon: 'fa-gavel', color: 'text-ThemeDangerDark' },
  [EESGCategory.Economic]: { label: 'Economic', icon: 'fa-money-bill-wave', color: 'text-ThemeWarningMedium' },
};

export const materialPillarMap: Record<MaterialPillar, { label: string; icon: string }> = {
  [MaterialPillar.People]: { label: 'People', icon: 'fa-people-group' },
  [MaterialPillar.Partnership]: { label: 'Partnership', icon: 'fa-handshake' },
  [MaterialPillar.Planet]: { label: 'Planet', icon: 'fa-planet-moon' },
  [MaterialPillar.Prosperity]: { label: 'Prosperity', icon: 'fa-seedling' },
  [MaterialPillar.Principle]: { label: 'Principle', icon: 'fa-file-signature' },
  [MaterialPillar.Profit]: { label: 'Profit', icon: 'fa-money-bill-wave' },
  [MaterialPillar.Purpose]: { label: 'Purpose', icon: 'fa-compass' },
};

export const boundaryMap = {
  [MaterialityBoundary.Leadership]: { label: 'Leadership' },
  [MaterialityBoundary.ResearchAndDevelopment]: { label: 'Research & Development' },
  [MaterialityBoundary.SupplyChain]: { label: 'Supply Chain' },
  [MaterialityBoundary.ProductAndServices]: { label: 'Product & Services' },
  [MaterialityBoundary.Distribution]: { label: 'Distribution' },
  [MaterialityBoundary.Communities]: { label: 'Communities' },
  [MaterialityBoundary.Experiences]: { label: 'Experiences' },
  [MaterialityBoundary.FinanceAndOperations]: { label: 'Finance & Operations' },
  [MaterialityBoundary.MarketingAndSales]: { label: 'Marketing & Sales' },
  [MaterialityBoundary.HumanResources]: { label: 'Human Resources' },
  [MaterialityBoundary.LegalAndCompliance]: { label: 'Legal & Compliance' },
  [MaterialityBoundary.TechnologyAndITInfrastructure]: { label: 'Technology & IT Infrastructure' },
  [MaterialityBoundary.PartnershipsAndAlliances]: { label: 'Partnerships & Alliances' },
  [MaterialityBoundary.EnvironmentalAndSustainability]: { label: 'Environmental & Sustainability' },
};

export const isDoubleMaterialitySurvey = (assessment: Pick<MaterialitySurveyModelMinData, 'assessmentType'>) => {
  return assessment.assessmentType === AssessmentType.DoubleMateriality;
};

export const isDoubleMaterialityAssessmentData = (
  data: PrioritizedAssessmentData | PrioritizedAssessmentData[],
): data is DoubleMaterialityAssessmentData | DoubleMaterialityAssessmentData[] => {
  if (Array.isArray(data)) {
    return data.every((item) => 'financialScore' in item && 'nonFinancialScore' in item);
  }
  return 'financialScore' in data && 'nonFinancialScore' in data;
};

export const topicLengthMap: { [key in MaterialityAssessmentScope]: number } = {
  [MaterialityAssessmentScope.Startup]: 15,
  [MaterialityAssessmentScope.Solopreneur]: 15,
  [MaterialityAssessmentScope.Micro]: 20,
  [MaterialityAssessmentScope.SME]: 25,
  [MaterialityAssessmentScope.MidCap]: 30,
  [MaterialityAssessmentScope.MNC]: 35,
  [MaterialityAssessmentScope.Large]: 40,
};

export const getAssessmentType = (assessment: Pick<MaterialitySurveyModelMinData, 'assessmentType' | 'type'> | undefined) => {
  if (!assessment) {
    return;
  }
  const { type, assessmentType } = assessment;
  if (!assessmentType) {
    return type === SurveyType.Materiality ? AssessmentType.FinancialMateriality : undefined;
  }

  return assessmentType;
};

export const getAssessmentTypeLabel = (
  assessment: Pick<MaterialitySurveyModelMinData, 'assessmentType' | 'type'> | undefined,
) => {
  const assessmentType = getAssessmentType(assessment);
  return assessmentType ? assessmentTypeLabels[assessmentType] : '';
};

// TODO: update the check after migration
export const isAssessmentPaid = (survey: Pick<MaterialitySurveyModelMinData, 'payment'>) => {
  return !survey.payment || survey.payment.status === 'paid';
};

export const getAssessmentIcon = ({
  survey,
  ignorePaymentStatus = true,
}: {
  survey: Pick<MinimalSurveyData, '_id' | 'assessmentType'| 'payment'>;
  ignorePaymentStatus?: boolean;
}) => {
  if (!ignorePaymentStatus && !isAssessmentPaid(survey)) {
    return 'fal fa-dollar-sign';
  }

  if (isDoubleMaterialitySurvey(survey)) {
    return 'fal fa-earth-europe';
  }
  return 'fal fa-buildings';
};
